import React from "react";
import { Button, Drawer, Tag, Tooltip } from "antd";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Globe,
  Home,
  Link2,
  RefreshCw,
  Shield,
} from "react-feather";

const statusMeta = {
  synced: { label: "Healthy", tone: "healthy", Icon: CheckCircle },
  saving: { label: "Syncing", tone: "syncing", Icon: RefreshCw },
  partial: { label: "Needs attention", tone: "warning", Icon: AlertTriangle },
  error: { label: "Issue", tone: "error", Icon: AlertTriangle },
  idle: { label: "Ready", tone: "neutral", Icon: Shield },
};

function statusFor(value) {
  return statusMeta[value] || statusMeta.idle;
}

function TrustRow({ icon: Icon, title, detail, status, action }) {
  const meta = statusFor(status);
  return (
    <div className="dist-trust-row">
      <div className={`dist-trust-row-icon is-${meta.tone}`}>
        <Icon size={16} />
      </div>
      <div className="dist-trust-row-copy">
        <div className="dist-trust-row-title">{title}</div>
        <div className="dist-trust-row-detail">{detail}</div>
      </div>
      <div className="dist-trust-row-side">
        <Tag className={`dist-trust-tag is-${meta.tone}`}>{meta.label}</Tag>
        {action || null}
      </div>
    </div>
  );
}

export default function DistributionTrustCenter({
  open,
  onClose,
  health,
  operation,
  mapping,
  verification,
  recovery,
  retrying,
  verifying,
  onRetry,
  onVerify,
  onOpenMapping,
  channelLabel,
}) {
  const meta = statusFor(health?.status);
  const OverallIcon = meta.Icon;
  const mismatchCount = Number(verification?.summary?.mismatch_checks || 0);
  const totalChecks = Number(verification?.summary?.total_checks || 0);
  const hasVerification = totalChecks > 0 || Array.isArray(verification?.mismatches);

  const mappingStatus =
    mapping?.status === "error"
      ? "error"
      : mapping?.unmappedCount > 0
        ? "partial"
        : mapping?.totalRooms > 0
          ? "synced"
          : "idle";

  const verifyStatus = !hasVerification
    ? "idle"
    : mismatchCount > 0
      ? "partial"
      : "synced";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      placement="right"
      destroyOnClose={false}
      zIndex={6900}
      className="dist-trust-drawer"
      title={
        <div className="dist-trust-drawer-title">
          <Shield size={18} />
          <div>
            <strong>Distribution Trust Center</strong>
            <span>{channelLabel || "Channel Manager"}</span>
          </div>
        </div>
      }
    >
      <div className={`dist-trust-overall is-${meta.tone}`}>
        <div className="dist-trust-overall-icon">
          <OverallIcon size={22} className={health?.status === "saving" ? "is-spinning" : ""} />
        </div>
        <div className="dist-trust-overall-copy">
          <span>Distribution health</span>
          <strong>{meta.label}</strong>
          <p>{health?.message || "Distribution controls are ready."}</p>
        </div>
        {health?.updatedAt ? <time>{health.updatedAt}</time> : null}
      </div>

      {operation?.id ? (
        <div className="dist-trust-operation">
          <div>
            <span>Latest operation</span>
            <strong>{operation.label || "Distribution update"}</strong>
          </div>
          <Tooltip title="Use this reference when tracing a save or retry in the UI session.">
            <code>{operation.id}</code>
          </Tooltip>
        </div>
      ) : null}

      {recovery ? (
        <section className="dist-trust-recovery">
          <div className="dist-trust-section-head">
            <div>
              <span>Recovery required</span>
              <strong>{recovery.label || "A distribution step needs attention"}</strong>
            </div>
            <Tag color="orange">{recovery.tasks?.length || 0} pending</Tag>
          </div>
          {Array.isArray(recovery.succeededTargets) && recovery.succeededTargets.length ? (
            <div className="dist-trust-recovery-success">
              <CheckCircle size={14} />
              Already completed: {recovery.succeededTargets.join(", ")}
            </div>
          ) : null}
          <div className="dist-trust-recovery-tasks">
            {(recovery.tasks || []).map((task) => (
              <div key={task.id || `${task.kind}-${task.target}`}>
                <AlertTriangle size={14} />
                <span>{task.target || "Distribution target"}</span>
              </div>
            ))}
          </div>
          <Button
            type="primary"
            icon={<RefreshCw size={14} />}
            loading={retrying}
            disabled={retrying || !(recovery.tasks || []).length}
            onClick={onRetry}
          >
            Retry failed step{(recovery.tasks || []).length === 1 ? "" : "s"}
          </Button>
          <small>Successful destinations are not re-run.</small>
        </section>
      ) : null}

      <div className="dist-trust-section-label">Live safeguards</div>

      <div className="dist-trust-list">
        <TrustRow
          icon={Activity}
          title="One inventory"
          detail="Availability and selling rules use the existing Channel Manager → Channex path and the Booking Engine manual calendar."
          status={health?.status === "saving" ? "saving" : recovery ? "partial" : health?.status === "error" ? "error" : "synced"}
        />

        <TrustRow
          icon={Home}
          title="Booking Engine room mapping"
          detail={
            mapping?.status === "error"
              ? "Booking Engine room types could not be loaded safely."
              : mapping?.totalRooms
                ? `${mapping.mappedCount || 0} of ${mapping.totalRooms} Channel Manager room type(s) have an exact shared Booking Engine ID.`
                : "No Channel Manager room types are visible in this range."
          }
          status={mappingStatus}
          action={
            mapping?.unmappedCount > 0 && onOpenMapping ? (
              <Button size="small" onClick={onOpenMapping}>Review</Button>
            ) : null
          }
        />

        <TrustRow
          icon={Globe}
          title="Channex readback"
          detail={
            !hasVerification
              ? "Not verified in this session. Run an explicit readback for the selected room and visible range."
              : mismatchCount > 0
                ? `${mismatchCount} of ${totalChecks} verification check(s) do not match NetShine desired state.`
                : `${totalChecks} verification check(s) matched NetShine desired state.`
          }
          status={verifyStatus}
          action={
            onVerify ? (
              <Button size="small" loading={verifying} onClick={onVerify}>
                {hasVerification ? "Verify again" : "Verify"}
              </Button>
            ) : null
          }
        />

        <TrustRow
          icon={Link2}
          title="Pricing separation"
          detail="Booking Engine Direct pricing and OTA pricing remain independent. A global inventory action does not collapse the two price engines."
          status="synced"
        />
      </div>

      {mapping?.unmappedRooms?.length ? (
        <section className="dist-trust-unmapped">
          <div className="dist-trust-section-head">
            <div>
              <span>Mapping blockers</span>
              <strong>{mapping.unmappedRooms.length} room type(s) blocked from global Direct writes</strong>
            </div>
          </div>
          <div className="dist-trust-unmapped-list">
            {mapping.unmappedRooms.slice(0, 8).map((room) => (
              <div key={room.id}>
                <AlertTriangle size={13} />
                <span>{room.name}</span>
                <code>{room.id}</code>
              </div>
            ))}
          </div>
          {onOpenMapping ? (
            <Button block onClick={onOpenMapping}>Open channel setup &amp; mapping</Button>
          ) : null}
        </section>
      ) : null}

      <div className="dist-trust-footnote">
        <Shield size={14} />
        NetShine only treats an exact shared room-type ID as a safe Direct mapping. Room names are never guessed.
      </div>
    </Drawer>
  );
}
